// ReviewQueue.tsx — the clinician's review desk (Review Mode). Lists every note,
// grouped ACT → Chapter in programme order, each tappable to jump straight to that
// content, editable and deletable, with three ways to export the lot (clipboard,
// a downloaded/shared .md file). Opened from Settings; mounted at app level so a
// "jump to" can open the step viewer.
import React from 'react';
import { Screen } from '@/components/Screen';
import { Body, Meta, Btn, Pressable, Display } from '@/components/ui';
import { Icon } from '@/components/icons';
import { useProgress } from '@/hooks/useProgress';
import { labelPartsFor, type ReviewLabelParts } from '@/services/reviewLabels';
import { buildReviewMarkdown, copyReviewMarkdown, exportReviewMarkdown } from '@/services/reviewExport';
import { findStep, findModule } from '@/services/contentProvider';
import type { Programme, Step } from '@/types';
import type { ReviewComment } from '@/services/progress';

export function ReviewQueue({
  programme,
  onOpenStep,
  onClose,
}: {
  programme: Programme;
  onOpenStep: (step: Step) => void;
  onClose: () => void;
}) {
  const progress = useProgress();
  const comments = progress.state.reviewComments;
  const [editing, setEditing] = React.useState<string | null>(null);
  const [draft, setDraft] = React.useState('');
  const [toast, setToast] = React.useState<string | null>(null);

  const flash = (m: string) => {
    setToast(m);
    window.setTimeout(() => setToast(null), 2200);
  };

  // Order every note by ACT → Chapter → step → time, then split into act/chapter groups.
  const ordered = comments
    .map((comment) => ({ comment, parts: labelPartsFor(programme, comment) }))
    .sort(
      (a, b) =>
        a.parts.actOrder - b.parts.actOrder ||
        a.parts.chapterOrder - b.parts.chapterOrder ||
        a.parts.stepOrder - b.parts.stepOrder ||
        a.comment.createdAt.localeCompare(b.comment.createdAt),
    );

  const jump = (c: ReviewComment) => {
    if (c.kind === 'step') {
      const step = findStep(programme, c.targetId);
      if (step) { onClose(); onOpenStep(step); return; }
    } else {
      const mod = findModule(programme, c.targetId);
      if (mod && mod.steps[0]) { onClose(); onOpenStep(mod.steps[0]); return; }
    }
    flash('Could not find that content.');
  };

  const copy = async () => {
    const md = buildReviewMarkdown(programme, comments, new Date().toISOString());
    flash((await copyReviewMarkdown(md)) ? 'Copied to clipboard' : 'Clipboard unavailable');
  };
  const exportFile = async () => {
    const md = buildReviewMarkdown(programme, comments, new Date().toISOString());
    try { await exportReviewMarkdown(md); } catch { flash('Export cancelled'); }
  };

  // Render groups with running act/chapter headers.
  let curAct = '';
  let curChapter = '';

  return (
    <Screen label="Review notes">
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12 }}>
        <Display parts={{ pre: 'Review ', em: 'notes', post: '' }} size={26} />
        <Pressable
          as="button"
          onClick={onClose}
          ariaLabel="Close"
          style={{ width: 40, height: 40, borderRadius: 999, border: '1px solid var(--hair)', background: 'var(--card-top)', color: 'var(--ink)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}
        >
          <Icon name="chevDown" size={18} />
        </Pressable>
      </div>
      <Body dim size={13.5} style={{ marginTop: 6 }}>
        {comments.length} note{comments.length === 1 ? '' : 's'} on the content as it’s presented. Tap a note to open that screen.
      </Body>

      {comments.length > 0 && (
        <div style={{ display: 'flex', gap: 10, marginTop: 16 }}>
          <Btn variant="ghost" onClick={copy} style={{ flex: 1 }}>
            Copy all
          </Btn>
          <Btn onClick={exportFile} style={{ flex: 1 }} leading={<Icon name="download" size={16} />}>
            Export file
          </Btn>
        </div>
      )}

      {comments.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '40px 16px' }}>
          <Body dim size={14}>
            No notes yet. Turn on Review Mode, open any lesson, and tap the pencil at the edge of the screen to leave one.
          </Body>
        </div>
      ) : (
        <div style={{ marginTop: 20 }}>
          {ordered.map(({ comment, parts }) => {
            const showAct = parts.act !== curAct;
            if (showAct) { curAct = parts.act; curChapter = ''; }
            const showChapter = parts.chapter !== curChapter;
            if (showChapter) curChapter = parts.chapter;
            return (
              <React.Fragment key={comment.id}>
                {showAct && (
                  <div style={{ fontFamily: 'Inter, system-ui, sans-serif', fontSize: 10.5, fontWeight: 700, letterSpacing: '0.14em', textTransform: 'uppercase', color: 'var(--amber-deep)', margin: '18px 2px 8px' }}>
                    {parts.act}
                  </div>
                )}
                {showChapter && (
                  <div style={{ fontFamily: 'Fraunces, Georgia, serif', fontSize: 16, color: 'var(--ink)', margin: '6px 2px 8px' }}>
                    {parts.chapter}
                  </div>
                )}
                <ReviewRow
                  comment={comment}
                  parts={parts}
                  editing={editing === comment.id}
                  draft={draft}
                  onJump={() => jump(comment)}
                  onStartEdit={() => { setEditing(comment.id); setDraft(comment.text); }}
                  onChangeDraft={setDraft}
                  onSaveEdit={() => { progress.updateReviewComment(comment.id, draft); setEditing(null); }}
                  onCancelEdit={() => setEditing(null)}
                  onDelete={() => progress.deleteReviewComment(comment.id)}
                />
              </React.Fragment>
            );
          })}
        </div>
      )}

      {toast && (
        <div style={{ position: 'fixed', left: 0, right: 0, bottom: 'calc(28px + var(--safe-bottom))', display: 'flex', justifyContent: 'center', zIndex: 120, pointerEvents: 'none' }}>
          <div style={{ background: 'rgba(28,22,16,0.92)', color: '#fff', borderRadius: 999, padding: '10px 18px', fontFamily: 'Inter, system-ui, sans-serif', fontSize: 13.5, fontWeight: 600 }}>
            {toast}
          </div>
        </div>
      )}
    </Screen>
  );
}

function ReviewRow({
  comment,
  parts,
  editing,
  draft,
  onJump,
  onStartEdit,
  onChangeDraft,
  onSaveEdit,
  onCancelEdit,
  onDelete,
}: {
  comment: ReviewComment;
  parts: ReviewLabelParts;
  editing: boolean;
  draft: string;
  onJump: () => void;
  onStartEdit: () => void;
  onChangeDraft: (v: string) => void;
  onSaveEdit: () => void;
  onCancelEdit: () => void;
  onDelete: () => void;
}) {
  return (
    <div style={{ border: '1px solid var(--hair)', borderRadius: 14, padding: '12px 13px', marginBottom: 10, background: 'var(--card-top)' }}>
      <Pressable
        as="button"
        onClick={onJump}
        ariaLabel="Open this content"
        style={{ display: 'flex', alignItems: 'center', gap: 8, width: '100%', textAlign: 'left', background: 'none', border: 'none', cursor: 'pointer', padding: 0, marginBottom: 8 }}
      >
        <span style={{ flex: 1, minWidth: 0, fontFamily: 'Inter, system-ui, sans-serif', fontSize: 12.5, fontWeight: 600, color: '#6a5cb0' }}>
          {comment.kind === 'chapter' ? 'Chapter note' : parts.step}
        </span>
        <Icon name="chevRight" size={15} />
      </Pressable>

      {editing ? (
        <>
          <textarea
            value={draft}
            onChange={(e) => onChangeDraft(e.target.value)}
            autoFocus
            style={{ width: '100%', minHeight: 80, boxSizing: 'border-box', resize: 'none', borderRadius: 11, border: '1px solid var(--hair-strong)', background: '#fff', padding: 11, color: 'var(--ink)', fontFamily: 'Inter, system-ui, sans-serif', fontSize: 15, lineHeight: 1.5, outline: 'none' }}
          />
          <div style={{ display: 'flex', gap: 8, marginTop: 9 }}>
            <Btn variant="ghost" onClick={onCancelEdit} style={{ flex: 1 }}>Cancel</Btn>
            <Btn onClick={onSaveEdit} style={{ flex: 1 }}>Save</Btn>
          </div>
        </>
      ) : (
        <>
          <Body size={14.5} style={{ whiteSpace: 'pre-wrap' }}>{comment.text}</Body>
          <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginTop: 9 }}>
            <Meta style={{ flex: 1 }}>
              <code>{comment.targetId}</code> · {comment.updatedAt.slice(0, 10)}
            </Meta>
            <Pressable as="button" onClick={onStartEdit} ariaLabel="Edit note" style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--green-deep)', fontFamily: 'Inter, system-ui, sans-serif', fontSize: 12.5, fontWeight: 600, padding: '4px 2px' }}>
              Edit
            </Pressable>
            <Pressable as="button" onClick={onDelete} ariaLabel="Delete note" style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'rgba(156,61,44,0.9)', fontFamily: 'Inter, system-ui, sans-serif', fontSize: 12.5, fontWeight: 600, padding: '4px 2px' }}>
              Delete
            </Pressable>
          </div>
        </>
      )}
    </div>
  );
}
